import { useState, useCallback, type MouseEvent } from 'react'
import { NOSTR_SIGNING_ERROR } from '@/lib/nostr'
import { useAuth } from '@/hooks/useAuth'
import { useProtocol } from '@/hooks/useProtocol'
import { useLinkCard } from '@/hooks/useLinkCard'
import { useVideoUpload } from '@/hooks/useVideoUpload'
import { Agent, RichText } from '@atproto/api'
import { useToast } from '@/hooks/useToast'
import { isSafeHostname } from '@/lib/apUtils'
import { resolveLocalStatusId } from '@/services/mastodonService'
import type { ReplyContext, QuoteContext } from '@/types/posts'

const BLUESKY_BLOB_MAX = 1_000_000
const BLUESKY_VIDEO_MAX = 100 * 1024 * 1024 // 100 MB
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime']

async function compressImage(
    file: File,
    maxBytes: number
): Promise<{ data: Uint8Array; type: string }> {
    // If already small enough, return as-is
    if (file.size <= maxBytes) {
        return { data: new Uint8Array(await file.arrayBuffer()), type: file.type }
    }

    const bitmap = await createImageBitmap(file)
    const canvas = document.createElement('canvas')
    let { width, height } = bitmap

    // Scale down if dimensions are very large
    const MAX_DIM = 2048
    if (width > MAX_DIM || height > MAX_DIM) {
        const scale = MAX_DIM / Math.max(width, height)
        width = Math.round(width * scale)
        height = Math.round(height * scale)
    }

    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    // Try JPEG at decreasing quality until under the limit
    for (const quality of [0.9, 0.8, 0.7, 0.5, 0.3]) {
        const blob = await new Promise<Blob>(resolve =>
            canvas.toBlob(b => resolve(b!), 'image/jpeg', quality)
        )
        if (blob.size <= maxBytes) {
            return { data: new Uint8Array(await blob.arrayBuffer()), type: 'image/jpeg' }
        }
    }

    // Last resort: scale down further and re-draw from the already-drawn canvas
    const halfWidth = Math.round(width * 0.5)
    const halfHeight = Math.round(height * 0.5)
    const imageData = ctx.getImageData(0, 0, width, height)
    canvas.width = halfWidth
    canvas.height = halfHeight
    const tmpCanvas = document.createElement('canvas')
    tmpCanvas.width = width
    tmpCanvas.height = height
    const tmpCtx = tmpCanvas.getContext('2d')
    if (!tmpCtx) throw new Error('Could not get canvas context for image compression')
    tmpCtx.putImageData(imageData, 0, 0)
    ctx.drawImage(tmpCanvas, 0, 0, halfWidth, halfHeight)
    const finalBlob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(b => resolve(b), 'image/jpeg', 0.5)
    )
    if (!finalBlob || finalBlob.size > maxBytes) {
        throw new Error('Image is too large and could not be compressed to under 1 MB')
    }
    return { data: new Uint8Array(await finalBlob.arrayBuffer()), type: 'image/jpeg' }
}

export interface MediaAttachment {
    id: string
    file: File
    preview: string
    altText: string
    type: 'image' | 'video'
    aspectRatio?: { width: number; height: number }
}

export interface PostResult {
    uri: string
    cid: string
    text: string
}

interface UsePostOptions {
    replyTo?: ReplyContext | null
    quoteOf?: QuoteContext | null
    onBeforeSubmit?: (text: string) => void
    onSuccess?: (result?: PostResult) => void | Promise<void>
    onError?: () => void
    selfLabel?: string
    langs?: string[]
}

export function usePost(options?: UsePostOptions) {
    const { session, mastodonSession, pixelfedSession, nostrSession, requestNostrReauth } =
        useAuth()
    const { selectedProtocol } = useProtocol()
    const { showToast } = useToast()
    const videoUpload = useVideoUpload()
    const [isPosting, setIsPosting] = useState(false)
    const [postText, setPostText] = useState('')
    const [selectedImages, setSelectedImages] = useState<MediaAttachment[]>([])
    const [mastodonVisibility, setMastodonVisibility] = useState<
        'public' | 'unlisted' | 'private' | 'direct'
    >('public')

    const {
        linkCard,
        dismissLinkCard,
        resetLinkCard,
        prefetchFromDraft,
        draftLinkHydration,
        acceptResolvedDraftLink,
        dismissDraftLink,
    } = useLinkCard({
        postText,
        selectedProtocol,
        hasImages: selectedImages.length > 0,
    })

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files) return

        const fileArray = Array.from(files)
        const firstFile = fileArray[0]
        if (!firstFile) return

        const isVideo = firstFile.type.startsWith('video/')

        if (isVideo) {
            // Only one video allowed; reject if images already attached
            if (selectedImages.length > 0) {
                showToast('Remove existing attachments before adding a video', 'error')
                e.target.value = ''
                return
            }

            if (!ALLOWED_VIDEO_TYPES.includes(firstFile.type)) {
                showToast('Only MP4 and QuickTime videos are supported', 'error')
                e.target.value = ''
                return
            }

            if (firstFile.size > BLUESKY_VIDEO_MAX) {
                const sizeMB = (firstFile.size / (1024 * 1024)).toFixed(1)
                showToast(`Video is too large (${sizeMB} MB). Maximum is 100 MB.`, 'error')
                e.target.value = ''
                return
            }

            const preview = URL.createObjectURL(firstFile)
            // preview is reused for the video element below to avoid creating a second
            // object URL. Revocation is deferred to removeImage/reset, both of which
            // call URL.revokeObjectURL(preview). BaseComposeModal calls reset() on
            // unmount, so the URL is always cleaned up on modal close.

            // Begin upload immediately on attach, while aspect ratio loads in parallel
            if (session) {
                videoUpload.start(firstFile, new Agent(session))
            }

            // Extract aspect ratio from the video element
            const videoEl = document.createElement('video')
            videoEl.preload = 'metadata'
            videoEl.onloadedmetadata = () => {
                const width = videoEl.videoWidth
                const height = videoEl.videoHeight
                const aspectRatio = width > 0 && height > 0 ? { width, height } : undefined
                setSelectedImages([
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        file: firstFile,
                        preview,
                        altText: '',
                        type: 'video',
                        aspectRatio,
                    },
                ])
            }
            videoEl.onerror = () => {
                setSelectedImages([
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        file: firstFile,
                        preview,
                        altText: '',
                        type: 'video',
                    },
                ])
            }
            videoEl.src = preview
        } else {
            // Images: reject if a video is already attached
            if (selectedImages.some(a => a.type === 'video')) {
                showToast('Remove the video before adding images', 'error')
                e.target.value = ''
                return
            }

            const newImages: MediaAttachment[] = fileArray
                .slice(0, 4 - selectedImages.length)
                .map(file => ({
                    id: Math.random().toString(36).substr(2, 9),
                    file,
                    preview: URL.createObjectURL(file),
                    altText: '',
                    type: 'image' as const,
                }))

            setSelectedImages(prev => [...prev, ...newImages].slice(0, 4))
        }

        // Reset so re-selecting the same file triggers onChange again
        e.target.value = ''
    }

    const removeImage = (id: string) => {
        setSelectedImages(prev => {
            const attachment = prev.find(a => a.id === id)
            if (attachment) {
                URL.revokeObjectURL(attachment.preview)
                if (attachment.type === 'video') videoUpload.reset()
            }
            return prev.filter(a => a.id !== id)
        })
    }

    const updateImageAltText = (id: string, altText: string) => {
        setSelectedImages(prev => prev.map(img => (img.id === id ? { ...img, altText } : img)))
    }

    const handlePost = async (e?: MouseEvent) => {
        e?.preventDefault()
        if (!postText.trim() && selectedImages.length === 0) return

        setIsPosting(true)
        options?.onBeforeSubmit?.(postText)
        try {
            let createdPost: PostResult | undefined
            // Post based on selected protocol
            if (selectedProtocol === 'bluesky' && session) {
                const agent = new Agent(session)
                const rt = new RichText({ text: postText })
                await rt.detectFacets(agent)

                // Separate images from video
                const videoAttachment = selectedImages.find(a => a.type === 'video')
                const imageAttachments = selectedImages.filter(a => a.type === 'image')

                // Upload images if any (compress to fit Bluesky's 1MB limit)
                const images = []
                for (const img of imageAttachments) {
                    const { data, type } = await compressImage(img.file, BLUESKY_BLOB_MAX)
                    const response = await agent.uploadBlob(data, {
                        encoding: type,
                    })
                    images.push({
                        alt: img.altText,
                        image: response.data.blob,
                    })
                }

                // Upload video if present
                let videoEmbed: { $type: string; [k: string]: unknown } | undefined
                if (videoAttachment) {
                    if (!videoUpload.blobRef) {
                        throw new Error('Video is not ready — please wait for upload to complete')
                    }
                    const videoBlob = videoUpload.blobRef

                    videoEmbed = {
                        $type: 'app.bsky.embed.video',
                        video: videoBlob,
                        ...(videoAttachment.altText ? { alt: videoAttachment.altText } : {}),
                        ...(videoAttachment.aspectRatio
                            ? { aspectRatio: videoAttachment.aspectRatio }
                            : {}),
                    }
                }

                // Build embed based on images, video, quote context, and link card
                // AT Protocol quotes require both uri and cid for embed.record
                const quoteRef = options?.quoteOf?.cid
                    ? {
                          $type: 'app.bsky.embed.record' as const,
                          record: { uri: options.quoteOf.uri, cid: options.quoteOf.cid },
                      }
                    : undefined

                let embed: { $type: string; [k: string]: unknown } | undefined
                if (videoEmbed && quoteRef) {
                    // Quote + video: use recordWithMedia
                    embed = {
                        $type: 'app.bsky.embed.recordWithMedia',
                        record: quoteRef,
                        media: videoEmbed,
                    }
                } else if (videoEmbed) {
                    embed = videoEmbed
                } else if (images.length > 0 && quoteRef) {
                    // Quote + images: use recordWithMedia
                    embed = {
                        $type: 'app.bsky.embed.recordWithMedia',
                        record: quoteRef,
                        media: {
                            $type: 'app.bsky.embed.images',
                            images,
                        },
                    }
                } else if (quoteRef) {
                    embed = quoteRef
                } else if (images.length > 0) {
                    embed = {
                        $type: 'app.bsky.embed.images',
                        images,
                    }
                } else if (linkCard?.metadata) {
                    // Build external embed with optional thumbnail
                    let thumb: unknown
                    if (linkCard.metadata.image) {
                        try {
                            const imageUrl = new URL(linkCard.metadata.image)
                            if (imageUrl.protocol === 'https:') {
                                const imgRes = await fetch(linkCard.metadata.image)
                                const contentType = imgRes.headers.get('content-type') ?? ''
                                if (contentType.startsWith('image/')) {
                                    const imgBuf = await imgRes.arrayBuffer()
                                    const blobRes = await agent.uploadBlob(new Uint8Array(imgBuf), {
                                        encoding: contentType,
                                    })
                                    thumb = blobRes.data.blob
                                }
                            }
                        } catch {
                            // Graceful fallback: post without thumbnail
                        }
                    }
                    embed = {
                        $type: 'app.bsky.embed.external',
                        external: {
                            uri: linkCard.metadata.url,
                            title: linkCard.metadata.title,
                            description: linkCard.metadata.description,
                            ...(thumb ? { thumb } : {}),
                        },
                    }
                }

                const replyRef =
                    options?.replyTo?.parent.cid && options?.replyTo?.root.cid
                        ? {
                              root: {
                                  uri: options.replyTo.root.uri,
                                  cid: options.replyTo.root.cid,
                              },
                              parent: {
                                  uri: options.replyTo.parent.uri,
                                  cid: options.replyTo.parent.cid,
                              },
                          }
                        : undefined

                const postResult = await agent.post({
                    text: rt.text,
                    facets: rt.facets,
                    createdAt: new Date().toISOString(),
                    reply: replyRef,
                    embed,
                    ...(options?.selfLabel
                        ? {
                              labels: {
                                  $type: 'com.atproto.label.defs#selfLabels',
                                  values: [{ val: options.selfLabel }],
                              },
                          }
                        : {}),
                    ...(options?.langs && options.langs.length > 0 ? { langs: options.langs } : {}),
                })
                createdPost = { uri: postResult.uri, cid: postResult.cid, text: postText }
            } else if (selectedProtocol === 'mastodon' && mastodonSession) {
                // Upload images first if any
                const mediaIds: string[] = []
                for (const img of selectedImages) {
                    const imageFormData = new FormData()
                    imageFormData.append('file', img.file)
                    if (img.altText) {
                        imageFormData.append('description', img.altText)
                    }

                    const uploadRes = await fetch(`${mastodonSession.instance_url}/api/v2/media`, {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${mastodonSession.access_token}`,
                        },
                        body: imageFormData,
                    })

                    if (uploadRes.ok) {
                        const data = await uploadRes.json()
                        mediaIds.push(data.id)
                    }
                }

                // Create post with media IDs, optional reply threading, and optional quote
                const formData = new FormData()
                formData.append('status', postText)
                formData.append('visibility', mastodonVisibility)
                mediaIds.forEach(id => formData.append('media_ids[]', id))
                // For Mastodon replies: resolve the status ID (handles both native Mastodon
                // replies and bridged AT Protocol replies via Bridgy Fed)
                let replyStatusId = options?.replyTo?.mastodonStatusId
                if (!replyStatusId && options?.replyTo?.parent.uri.startsWith('at://')) {
                    try {
                        const bridgyUrl = `https://bsky.brid.gy/convert/ap/${options.replyTo.parent.uri}`
                        replyStatusId = await resolveLocalStatusId(
                            mastodonSession,
                            bridgyUrl,
                            bridgyUrl
                        )
                    } catch {
                        // If resolution fails, post without threading
                    }
                }
                if (replyStatusId) {
                    formData.append('in_reply_to_id', replyStatusId)
                }
                if (options?.quoteOf?.mastodonStatusId) {
                    formData.append('quoted_status_id', options.quoteOf.mastodonStatusId)
                }
                if (options?.selfLabel) {
                    formData.append('sensitive', 'true')
                }
                if (options?.langs && options.langs.length > 0) {
                    formData.append('language', options.langs[0])
                }

                const res = await fetch(`${mastodonSession.instance_url}/api/v1/statuses`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${mastodonSession.access_token}`,
                    },
                    body: formData,
                })

                if (!res.ok) throw new Error('Failed to post to Mastodon')
                const data = await res.json()
                createdPost = { uri: data.uri, cid: data.id, text: postText }
            } else if (selectedProtocol === 'pixelfed' && pixelfedSession) {
                // Pixelfed requires at least one photo
                if (selectedImages.length === 0) {
                    throw new Error('Pixelfed posts require at least one photo')
                }

                // Validate instance URL before making any requests
                const parsedInstanceUrl = new URL(pixelfedSession.instance_url)
                if (parsedInstanceUrl.protocol !== 'https:') {
                    throw new Error('Unsafe URL scheme')
                }
                if (!isSafeHostname(parsedInstanceUrl.hostname)) {
                    throw new Error('Unsafe hostname')
                }

                // Upload images first
                const mediaIds: string[] = []
                for (const img of selectedImages) {
                    const imageFormData = new FormData()
                    imageFormData.append('file', img.file)
                    if (img.altText) {
                        imageFormData.append('description', img.altText)
                    }

                    const uploadRes = await fetch(`${pixelfedSession.instance_url}/api/v2/media`, {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${pixelfedSession.access_token}`,
                        },
                        body: imageFormData,
                    })

                    if (!uploadRes.ok) {
                        throw new Error(`Media upload failed (${uploadRes.status})`)
                    }
                    const data = await uploadRes.json()
                    mediaIds.push(data.id)
                }

                // Post status with media IDs (text is optional on Pixelfed)
                const formData = new FormData()
                formData.append('status', postText)
                formData.append('visibility', mastodonVisibility)
                mediaIds.forEach(id => formData.append('media_ids[]', id))
                if (options?.replyTo?.mastodonStatusId) {
                    formData.append('in_reply_to_id', options.replyTo.mastodonStatusId)
                }

                const res = await fetch(`${pixelfedSession.instance_url}/api/v1/statuses`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${pixelfedSession.access_token}`,
                    },
                    body: formData,
                })

                if (!res.ok) throw new Error('Failed to post to Pixelfed')
                const data = await res.json()
                createdPost = { uri: data.uri, cid: data.id, text: postText }
            } else if (selectedProtocol === 'nostr' && nostrSession) {
                // Nostr post: publish kind 1 event
                const { createSignedEvent } = await import('@/lib/nostr')
                const { SimplePool } = await import('nostr-tools')
                const { sanitizeRelays, DEFAULT_RELAYS } = await import('@/lib/nostr')
                const relays = sanitizeRelays(nostrSession.relays ?? DEFAULT_RELAYS)

                const tags: string[][] = []
                // Reply tags (NIP-10)
                if (options?.replyTo) {
                    const parentEventId = options.replyTo.parent.uri.replace('nostr:', '')
                    const rootUri = options.replyTo.root?.uri ?? options.replyTo.parent.uri
                    const rootEventId = rootUri.replace('nostr:', '')
                    if (rootEventId === parentEventId) {
                        tags.push(['e', parentEventId, relays[0] ?? '', 'root'])
                    } else {
                        tags.push(['e', rootEventId, relays[0] ?? '', 'root'])
                        tags.push(['e', parentEventId, relays[0] ?? '', 'reply'])
                    }
                }

                const signed = await createSignedEvent(nostrSession, 1, postText, tags)
                const pool = new SimplePool()
                try {
                    await Promise.any(pool.publish(relays, signed))
                } finally {
                    pool.close(relays)
                }
                createdPost = { uri: `nostr:${signed.id}`, cid: signed.id, text: postText }
            } else {
                throw new Error('No session available for selected protocol')
            }

            setPostText('')
            setSelectedImages(prev => {
                prev.forEach(a => URL.revokeObjectURL(a.preview))
                return []
            })
            const toastMessage = options?.replyTo
                ? 'Reply posted!'
                : options?.quoteOf
                  ? 'Quote posted!'
                  : 'Post created successfully!'
            showToast(toastMessage, 'success')
            await options?.onSuccess?.(createdPost)
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : ''
            if (message.includes(NOSTR_SIGNING_ERROR)) {
                void requestNostrReauth()
                return
            }
            console.error('Post failed:', err)
            showToast('Post failed: ' + (message || 'Unknown error'), 'error')
            options?.onError?.()
        } finally {
            setIsPosting(false)
        }
    }

    const { reset: resetVideoUpload } = videoUpload
    const reset = useCallback(() => {
        resetVideoUpload()
        setPostText('')
        setSelectedImages(prev => {
            prev.forEach(img => URL.revokeObjectURL(img.preview))
            return []
        })
        resetLinkCard()
    }, [resetLinkCard, resetVideoUpload])

    return {
        postText,
        setPostText,
        isPosting,
        handlePost,
        mastodonVisibility,
        setMastodonVisibility,
        selectedImages,
        setSelectedImages,
        handleImageSelect,
        removeImage,
        updateImageAltText,
        reset,
        linkCard,
        dismissLinkCard,
        prefetchFromDraft,
        draftLinkHydration,
        acceptResolvedDraftLink,
        dismissDraftLink,
        videoUpload,
    }
}
